// src/components/admin/LocationsManagement.jsx
import React, { useState, useEffect } from 'react';
import { ArrowLeft, MapPin, Plus, Edit2, Trash2 } from 'lucide-react';
import { styles } from '../../styles/theme';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';

// Which center's payroll a location's hours go to. Locations saved before this
// field existed default by name: Kirkland → Kirkland, everything else → Redmond.
export const getPayrollCenter = (location) =>
  location.payrollCenter || ((location.name || '').toLowerCase().includes('kirkland') ? 'kirkland' : 'redmond');

const PAYROLL_CENTER_LABELS = { redmond: 'Redmond', kirkland: 'Kirkland' };

const LocationsManagement = ({ onBack }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    defaultStaffNeeded: 1,
    payrollCenter: '',
    active: true
  });

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const locationsRef = collection(db, 'locations');
      const snapshot = await getDocs(locationsRef);
      const locationsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLocations(locationsList);
    } catch (error) {
      console.error('Error loading locations:', error);
      alert('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingLocation(null);
    setFormData({
      name: '',
      address: '',
      defaultStaffNeeded: 1,
      payrollCenter: '',
      active: true
    });
    setShowModal(true);
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      address: location.address,
      defaultStaffNeeded: location.defaultStaffNeeded,
      payrollCenter: getPayrollCenter(location),
      active: location.active
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editingLocation) {
        // Update existing
        const locationRef = doc(db, 'locations', editingLocation.id);
        await updateDoc(locationRef, formData);
        alert('Location updated successfully!');
      } else {
        // Add new
        await addDoc(collection(db, 'locations'), {
          ...formData,
          createdAt: new Date().toISOString()
        });
        alert('Location added successfully!');
      }
      setShowModal(false);
      loadLocations();
    } catch (error) {
      console.error('Error saving location:', error);
      alert('Failed to save location');
    }
  };

  const handleDelete = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    
    try {
      await deleteDoc(doc(db, 'locations', locationId));
      alert('Location deleted successfully!');
      loadLocations();
    } catch (error) {
      console.error('Error deleting location:', error);
      alert('Failed to delete location');
    }
  };

  const handleTogglePause = async (location) => {
    const action = location.active ? 'pause' : 'resume';
    if (!window.confirm(`${action === 'pause' ? 'Pause' : 'Resume'} "${location.name}"? ${action === 'pause' ? 'It will be hidden from staff check-in.' : 'It will appear in staff check-in again.'}`)) return;
    try {
      await updateDoc(doc(db, 'locations', location.id), { active: !location.active });
      loadLocations();
    } catch (error) {
      alert('Failed to update location');
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button onClick={onBack} style={styles.secondaryButton}>
          <ArrowLeft size={16} />
          Back to Dashboard
        </button>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
              <MapPin style={{ display: 'inline', marginRight: '0.5rem' }} size={24} />
              Locations & Capacity
            </h1>
            <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>
              Manage program locations and default staff requirements
            </p>
          </div>
          
          <button onClick={handleAdd} style={styles.button}>
            <Plus size={16} />
            Add Location
          </button>
        </div>
      </div>

      {/* Locations List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>Loading locations...</div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {locations.map(location => (
            <div key={location.id} style={{ ...styles.card, padding: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{location.name}</h3>
                    {location.active ? (
                      <span style={{ ...styles.badge, backgroundColor: '#d1fae5', color: '#065f46' }}>✅ Active</span>
                    ) : (
                      <span style={{ ...styles.badge, backgroundColor: '#fef3c7', color: '#92400e' }}>⏸ Paused</span>
                    )}
                  </div>
                  <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    📍 {location.address}
                  </p>
                  <p style={{ color: '#3b82f6', fontSize: '0.875rem', fontWeight: '500' }}>
                    Default Staff Needed: {location.defaultStaffNeeded}
                  </p>
                  <p style={{ color: '#059669', fontSize: '0.875rem', fontWeight: '500', marginTop: '0.25rem' }}>
                    💰 Payroll: {PAYROLL_CENTER_LABELS[getPayrollCenter(location)]}
                    {!location.payrollCenter && <span style={{ color: '#9ca3af', fontWeight: '400' }}> (default — edit to confirm)</span>}
                  </p>
                </div>
                
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <button
                    onClick={() => handleTogglePause(location)}
                    title={location.active ? 'Pause — hide from staff check-in' : 'Resume — show in staff check-in'}
                    style={{ padding: '0.4rem 0.75rem', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '700', backgroundColor: location.active ? '#fef3c7' : '#d1fae5', color: location.active ? '#92400e' : '#065f46' }}
                  >
                    {location.active ? '⏸ Pause' : '▶ Resume'}
                  </button>
                  <button
                    onClick={() => handleEdit(location)}
                    style={{ ...styles.button, backgroundColor: '#3b82f6', padding: '0.5rem' }}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(location.id)}
                    style={{ ...styles.button, backgroundColor: '#dc2626', padding: '0.5rem' }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px'
          }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>
              {editingLocation ? 'Edit Location' : 'Add New Location'}
            </h2>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Location Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={styles.input}
                placeholder="e.g., BCA1, BCA2, Stemtree"
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Address *
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                style={styles.input}
                placeholder="Full address"
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Default Staff Needed *
              </label>
              <input
                type="number"
                min="1"
                value={formData.defaultStaffNeeded}
                onChange={(e) => setFormData({ ...formData, defaultStaffNeeded: parseInt(e.target.value) })}
                style={styles.input}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Payroll Charged To *
              </label>
              <select
                value={formData.payrollCenter}
                onChange={(e) => setFormData({ ...formData, payrollCenter: e.target.value })}
                style={styles.input}
              >
                <option value="">Select center...</option>
                <option value="redmond">Stemtree Redmond</option>
                <option value="kirkland">Stemtree Kirkland</option>
              </select>
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.35rem' }}>
                Staff hours worked here are included in this center's payroll in Time Reports.
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                />
                <span>Active</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  ...styles.button,
                  backgroundColor: '#6b7280'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                style={styles.button}
                disabled={!formData.name || !formData.address || !formData.payrollCenter}
              >
                {editingLocation ? 'Update' : 'Add'} Location
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationsManagement;